import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Car, Users, Briefcase, Calendar, MapPin, Plane, Phone, Mail, User as UserIcon,
  CheckCircle2, Loader2, CreditCard, Smartphone, Building2, ChevronLeft, ChevronRight,
} from "lucide-react";

const VEHICLE_TYPES = [
  { key: "economy", label: "Economy", desc: "Até 3 pax · 2 malas", icon: Car },
  { key: "executive", label: "Executive", desc: "Até 4 pax · 3 malas", icon: Car },
  { key: "xl_group", label: "XL / Grupo", desc: "Até 8 pax · 8 malas", icon: Users },
];

const FUEL_LABELS = {
  diesel: "Gasóleo", petrol: "Gasolina", hybrid: "Híbrido",
  plug_in_hybrid: "Híbrido plug-in", electric: "Elétrico", lpg: "GPL",
};

const PAYMENT_METHODS = [
  { key: "mbway", label: "MB Way", icon: Smartphone },
  { key: "multibanco", label: "Multibanco", icon: Building2 },
  { key: "card", label: "Credit card", icon: CreditCard },
];

const initialForm = {
  type: "transfer",
  vehicle_type: "executive",
  pickup_address: "",
  destination_address: "",
  service_datetime: "",
  service_duration_hours: 1.0,
  passengers: 2,
  luggage: 2,
  flight_number: "",
  special_requests: "",
  client_name: "",
  client_email: "",
  client_phone: "",
  payment_method: "mbway",
  invoice_nif: "",
  invoice_name: "",
};

export default function PartnerBookingPage() {
  const { slug } = useParams();
  const [partner, setPartner] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [categories, setCategories] = useState([]);
  const [siteInfo, setSiteInfo] = useState(null);

  useEffect(() => {
    api.get(`/public/partner/${slug}`)
      .then((r) => setPartner(r.data))
      .catch(() => setNotFound(true));
    api.get(`/public/vehicle-categories`).then((r) => setCategories(r.data)).catch(() => {});
    api.get(`/public/site`).then((r) => setSiteInfo(r.data)).catch(() => {});
  }, [slug]);

  const brand = partner?.branding || {};
  const primary = brand.primary_color || "#0a0f1e";
  const accent = brand.accent_color || "#f59e0b";
  const partnerLogo = partner?.logo_url || brand.logo_url || "";

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canQuote = useMemo(
    () => form.pickup_address && form.destination_address && form.vehicle_type,
    [form]
  );

  const fetchQuote = async () => {
    if (!canQuote) return;
    setQuoting(true);
    try {
      const { data } = await api.post(`/public/partner/${slug}/quote`, {
        pickup: form.pickup_address,
        destination: form.destination_address,
        vehicle_type: form.vehicle_type,
        service_datetime: form.service_datetime || null,
      });
      setQuote(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setQuote(null);
    } finally {
      setQuoting(false);
    }
  };

  useEffect(() => {
    if (!partner || !canQuote) return;
    const id = setTimeout(fetchQuote, 400);
    return () => clearTimeout(id);
  }, [form.pickup_address, form.destination_address, form.vehicle_type, form.service_datetime, partner]);

  const submitBooking = async () => {
    if (!form.client_name || !form.client_email || !form.client_phone) {
      toast.error("Por favor preencha nome, email e telefone.");
      return;
    }
    // Invoice spec #7: NIF filled → name required
    if (form.invoice_nif && !form.invoice_name) {
      toast.error("Indicou NIF na fatura — o nome do cliente na fatura passa a ser obrigatório.");
      return;
    }
    if (!form.service_datetime) {
      toast.error("Please choose service date and time.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/partner/${slug}/booking`, {
        type: form.type,
        client_name: form.client_name,
        client_email: form.client_email,
        client_phone: form.client_phone,
        service_datetime: new Date(form.service_datetime).toISOString(),
        service_duration_hours: Number(form.service_duration_hours) || 1.0,
        pickup_address: form.pickup_address,
        destination_address: form.destination_address,
        passengers: Number(form.passengers) || 1,
        luggage: Number(form.luggage) || 0,
        vehicle_type: form.vehicle_type,
        flight_number: form.flight_number || null,
        special_requests: form.special_requests || null,
        payment_method: form.payment_method,
        invoice_nif: form.invoice_nif || null,
        invoice_name: form.invoice_name || null,
      });
      setConfirmation(data);
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <div className="text-6xl mb-4" style={{ color: primary }}>404</div>
          <h1 className="text-2xl font-serif" style={{ color: primary }}>Partner not found</h1>
          <p className="text-slate-500 mt-2 text-sm">This booking page is not available or the partner has not been approved yet.</p>
        </div>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="partner-booking-page">
      <header className="border-b border-slate-200 bg-white" style={{ borderTopWidth: 4, borderTopColor: accent }}>
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {partnerLogo ? (
              <img src={partnerLogo} alt={partner.company_name} className="h-14 max-w-[220px] object-contain" data-testid="partner-logo" />
            ) : (
              <div className="w-12 h-12 flex items-center justify-center text-white font-serif text-xl" style={{ background: primary }}>
                {(partner.company_name || "").slice(0, 1)}
              </div>
            )}
            <div>
              <div className="text-xl font-serif" style={{ color: primary }} data-testid="partner-name">{partner.company_name}</div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Concierge · Transfers & tours privados</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Com tecnologia</div>
            <div className="text-sm font-serif" style={{ color: primary }}>DiTours</div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <Stepper step={step} accent={accent} />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div className="md:col-span-2 space-y-6">
            {step === 1 && (
              <TripCard form={form} setField={setField} accent={accent} primary={primary} categories={categories} />
            )}
            {step === 2 && (
              <ContactCard form={form} setField={setField} accent={accent} primary={primary} partner={partner} />
            )}
            {step === 3 && confirmation && (
              <ConfirmationCard confirmation={confirmation} accent={accent} primary={primary} />
            )}
          </div>

          <div className="md:col-span-1">
            <PriceSummary
              form={form}
              quote={quote}
              quoting={quoting}
              partner={partner}
              accent={accent}
              primary={primary}
              step={step}
              submitting={submitting}
              onNext={() => {
                if (step === 1) {
                  if (!form.pickup_address || !form.destination_address) {
                    toast.error("Please add pickup and destination.");
                    return;
                  }
                  if (!form.service_datetime) {
                    toast.error("Please choose service date and time.");
                    return;
                  }
                  setStep(2);
                } else if (step === 2) {
                  submitBooking();
                }
              }}
              onBack={() => step > 1 && step < 3 && setStep(step - 1)}
            />
          </div>
        </div>
      </div>

      <footer className="border-t border-slate-200 mt-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-8">
          {siteInfo && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pb-6 mb-6 border-b border-slate-100 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{siteInfo.company_name}</div>
                {siteInfo.company_address && <div className="text-slate-700 leading-relaxed" data-testid="footer-address">{siteInfo.company_address}</div>}
                {siteInfo.company_nif && <div className="text-slate-500 mt-1">NIF {siteInfo.company_nif}</div>}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Contactos</div>
                {siteInfo.support_phone && <a href={`tel:${siteInfo.support_phone}`} className="block text-slate-700 hover:text-[#f59e0b]" data-testid="footer-phone">{siteInfo.support_phone}</a>}
                {siteInfo.support_whatsapp && <a href={`https://wa.me/${siteInfo.support_whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="block text-slate-700 hover:text-[#f59e0b]">WhatsApp {siteInfo.support_whatsapp}</a>}
                {siteInfo.admin_email && <a href={`mailto:${siteInfo.admin_email}`} className="block text-slate-700 hover:text-[#f59e0b]" data-testid="footer-email">{siteInfo.admin_email}</a>}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Legal</div>
                <div className="space-y-1">
                  <a href="/termos" className="block text-slate-700 hover:text-[#f59e0b]">Termos & Condições</a>
                  <a href="/privacidade" className="block text-slate-700 hover:text-[#f59e0b]">Privacidade</a>
                  <a href="/cookies" className="block text-slate-700 hover:text-[#f59e0b]">Cookies</a>
                </div>
              </div>
            </div>
          )}
          <div className="text-xs text-slate-500 flex flex-wrap items-center justify-between gap-3">
            <div>© {new Date().getFullYear()} {partner.company_name} · Serviço de reservas por DiTours</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">RNAAT · Licenciado RNAV</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stepper({ step, accent }) {
  const items = ["Trip details", "Contact & Payment", "Confirmation"];
  return (
    <div className="flex items-center justify-center gap-3 text-xs uppercase tracking-widest">
      {items.map((label, i) => {
        const idx = i + 1;
        const active = step === idx;
        const done = step > idx;
        return (
          <React.Fragment key={label}>
            <div className="flex items-center gap-2" data-testid={`step-${idx}`}>
              <span
                className="w-6 h-6 flex items-center justify-center text-[11px] font-semibold"
                style={{
                  background: done || active ? accent : "#e2e8f0",
                  color: done || active ? "#0a0f1e" : "#64748b",
                }}
              >
                {done ? <CheckCircle2 className="w-3 h-3" /> : idx}
              </span>
              <span className={active ? "text-slate-900" : "text-slate-500"}>{label}</span>
            </div>
            {idx < items.length && <span className="text-slate-300">—</span>}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function TripCard({ form, setField, accent, primary, categories }) {
  const selectedCategory = (categories || []).find((c) => c.key === form.vehicle_type);
  const vehicles = selectedCategory?.vehicles || [];
  return (
    <div className="bg-white border border-slate-200 shadow-sm">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <h2 className="text-sm uppercase tracking-widest font-semibold" style={{ color: primary }}>Trip details</h2>
        <div className="flex gap-2 text-[11px] uppercase tracking-widest">
          {["transfer", "tour"].map((t) => (
            <button
              key={t}
              onClick={() => setField("type", t)}
              className="px-3 py-1 border"
              style={{
                borderColor: form.type === t ? accent : "#e2e8f0",
                background: form.type === t ? accent : "transparent",
                color: form.type === t ? primary : "#64748b",
              }}
              data-testid={`type-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Pickup address" icon={MapPin}>
            <Input value={form.pickup_address} onChange={(e) => setField("pickup_address", e.target.value)} placeholder="Airport Lisbon" data-testid="pickup-input" />
          </Field>
          <Field label="Destination address" icon={MapPin}>
            <Input value={form.destination_address} onChange={(e) => setField("destination_address", e.target.value)} placeholder="Cascais" data-testid="destination-input" />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Date & time" icon={Calendar}>
            <Input
              type="datetime-local"
              value={form.service_datetime}
              onChange={(e) => setField("service_datetime", e.target.value)}
              data-testid="datetime-input"
            />
          </Field>
          <Field label="Passengers" icon={Users}>
            <Input type="number" min={1} max={20} value={form.passengers} onChange={(e) => setField("passengers", e.target.value)} data-testid="passengers-input" />
          </Field>
          <Field label="Luggage" icon={Briefcase}>
            <Input type="number" min={0} max={20} value={form.luggage} onChange={(e) => setField("luggage", e.target.value)} data-testid="luggage-input" />
          </Field>
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-3">Vehicle class</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {VEHICLE_TYPES.map((v) => {
              const Icon = v.icon;
              const selected = form.vehicle_type === v.key;
              const cat = (categories || []).find((c) => c.key === v.key);
              const count = cat?.count ?? 0;
              return (
                <button
                  key={v.key}
                  onClick={() => setField("vehicle_type", v.key)}
                  className="text-left p-4 border transition-colors"
                  style={{
                    borderColor: selected ? accent : "#e2e8f0",
                    background: selected ? "rgba(245,158,11,0.08)" : "#fff",
                  }}
                  data-testid={`vehicle-${v.key}`}
                >
                  <Icon className="w-5 h-5 mb-2" style={{ color: selected ? primary : "#64748b" }} />
                  <div className="text-sm font-semibold" style={{ color: primary }}>{v.label}</div>
                  <div className="text-[11px] text-slate-500">{v.desc}</div>
                  {count > 0 && (
                    <div className="mt-2 text-[10px] uppercase tracking-widest text-emerald-700 font-bold">{count} disponível{count > 1 ? "eis" : ""}</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {vehicles.length > 0 && (
          <div data-testid="category-vehicles">
            <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-3">Veículos que podem operar {selectedCategory.label_pt}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {vehicles.slice(0, 6).map((v) => (
                <div key={v.id} className="border border-slate-200 overflow-hidden" data-testid={`op-vehicle-${v.id}`}>
                  <VehicleGallery photos={v.photos && v.photos.length ? v.photos : (v.photo ? [v.photo] : [])} label={`${v.brand} ${v.model}`} />
                  <div className="p-2">
                    <div className="text-xs font-semibold" style={{ color: primary }}>{v.brand} {v.model}</div>
                    <div className="text-[10px] text-slate-600 mt-0.5">{FUEL_LABELS[v.fuel_type] || v.fuel_type} · {v.capacity_passengers} pax · {v.capacity_luggage} mala</div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 mt-2">O veículo exacto é confirmado após aceitação. Todos os veículos desta categoria têm capacidade e características equivalentes.</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Flight number (optional)" icon={Plane}>
            <Input value={form.flight_number} onChange={(e) => setField("flight_number", e.target.value)} placeholder="TP1234" data-testid="flight-input" />
          </Field>
          <Field label="Duration (hours, tours)" icon={Calendar}>
            <Input type="number" min={0.5} step={0.5} value={form.service_duration_hours} onChange={(e) => setField("service_duration_hours", e.target.value)} data-testid="duration-input" />
          </Field>
        </div>

        <Field label="Special requests (optional)">
          <Textarea rows={3} value={form.special_requests} onChange={(e) => setField("special_requests", e.target.value)} placeholder="Child seat, silent trip, extra stops…" data-testid="special-requests-input" />
        </Field>
      </div>
    </div>
  );
}

function ContactCard({ form, setField, accent, primary, partner }) {
  const nifFilled = !!(form.invoice_nif || "").trim();
  const clientPaymentEnabled = !!partner?.allow_client_payment;
  return (
    <div className="bg-white border border-slate-200 shadow-sm">
      <div className="px-6 py-4 border-b border-slate-200">
        <h2 className="text-sm uppercase tracking-widest font-semibold" style={{ color: primary }}>Contacto & Pagamento</h2>
      </div>
      <div className="p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Nome completo" icon={UserIcon}>
            <Input value={form.client_name} onChange={(e) => setField("client_name", e.target.value)} placeholder="Ana Silva" data-testid="client-name-input" />
          </Field>
          <Field label="Email" icon={Mail}>
            <Input type="email" value={form.client_email} onChange={(e) => setField("client_email", e.target.value)} placeholder="ana@email.com" data-testid="client-email-input" />
          </Field>
        </div>
        <Field label="Telefone (com indicativo)" icon={Phone}>
          <Input value={form.client_phone} onChange={(e) => setField("client_phone", e.target.value)} placeholder="+351 91 234 5678" data-testid="client-phone-input" />
        </Field>
        <p className="text-[11px] text-slate-500 -mt-2">Enviamos o código da viagem para email e telefone.</p>

        {/* Invoice — optional */}
        <div className="pt-4 border-t border-slate-100">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-3">Fatura (opcional)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="NIF (opcional)">
              <Input value={form.invoice_nif} onChange={(e) => setField("invoice_nif", e.target.value)} placeholder="123456789" data-testid="invoice-nif-input" />
            </Field>
            <Field label={nifFilled ? "Nome na fatura (obrigatório)" : "Nome na fatura (opcional)"}>
              <Input value={form.invoice_name} onChange={(e) => setField("invoice_name", e.target.value)} placeholder={nifFilled ? "Nome fiscal completo" : "Ex. Ana Silva (deixe vazio para 'Consumidor final')"} data-testid="invoice-name-input" />
            </Field>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Sem NIF nem nome → fatura genérica <em>Consumidor final</em>. Com NIF, o nome é obrigatório.
          </p>
        </div>

        {clientPaymentEnabled && (
          <div className="pt-4 border-t border-slate-100">
            <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-3">Método de pagamento</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {PAYMENT_METHODS.map((p) => {
                const Icon = p.icon;
                const selected = form.payment_method === p.key;
                return (
                  <button
                    key={p.key}
                    onClick={() => setField("payment_method", p.key)}
                    className="text-left p-4 border transition-colors flex items-center gap-3"
                    style={{ borderColor: selected ? accent : "#e2e8f0", background: selected ? "rgba(245,158,11,0.08)" : "#fff" }}
                    data-testid={`payment-${p.key}`}
                  >
                    <Icon className="w-5 h-5" style={{ color: selected ? primary : "#64748b" }} />
                    <span className="text-sm font-semibold" style={{ color: primary }}>{p.label}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500 mt-3">Pagamentos processados pelo IfthenPay. Instruções por email após confirmação.</p>
          </div>
        )}
        {!clientPaymentEnabled && (
          <div className="pt-4 border-t border-slate-100 bg-slate-50 -mx-6 -mb-6 px-6 py-4">
            <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">Faturação</p>
            <p className="text-xs text-slate-700">Esta reserva será faturada diretamente ao parceiro <strong>{partner?.company_name || "—"}</strong> ao abrigo do acordo comercial existente. Não é solicitado pagamento ao cliente neste widget.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ConfirmationCard({ confirmation, accent, primary }) {
  const pay = confirmation.payment || {};
  return (
    <div className="bg-white border border-slate-200 shadow-sm" data-testid="confirmation-card">
      <div className="px-6 py-6 border-b border-slate-200 flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center" style={{ background: accent }}>
          <CheckCircle2 className="w-5 h-5" style={{ color: primary }} />
        </div>
        <div>
          <h2 className="text-lg font-serif" style={{ color: primary }}>Booking received</h2>
          <p className="text-xs text-slate-500">A concierge will confirm shortly by email and WhatsApp.</p>
        </div>
      </div>
      <div className="p-6 space-y-4">
        <Row label="Reference">
          <span className="font-mono text-base" style={{ color: primary }} data-testid="booking-reference">{confirmation.reference}</span>
        </Row>
        <Row label="Amount">
          <span className="text-base font-semibold" style={{ color: primary }}>€ {confirmation.client_pays?.toFixed(2)}</span>
        </Row>
        <Row label="Payment method">
          <span className="text-sm" style={{ color: primary }}>{pay.method?.toUpperCase()}</span>
        </Row>
        {pay.method === "multibanco" && (
          <>
            <Row label="Entidade"><span className="font-mono">{pay.multibanco_entity}</span></Row>
            <Row label="Referência"><span className="font-mono">{pay.multibanco_reference}</span></Row>
          </>
        )}
        {pay.method === "mbway" && (
          <Row label="MB Way notification"><span className="text-sm">Sent to your phone</span></Row>
        )}
        {pay.method === "card" && (
          <Row label="Card"><span className="text-sm">Redirect link sent by email</span></Row>
        )}
        <div className="text-[11px] text-slate-400 mt-4 border-t border-slate-100 pt-3">{pay.note}</div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-widest text-slate-500">{label}</span>
      {children}
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      {children}
    </div>
  );
}

function PriceSummary({ form, quote, quoting, partner, accent, primary, step, submitting, onNext, onBack }) {
  const price = quote?.client_pays;
  return (
    <div className="bg-white border border-slate-200 shadow-sm sticky top-6" data-testid="price-summary">
      <div className="px-5 py-4 border-b border-slate-200">
        <p className="text-[11px] uppercase tracking-widest text-slate-500">Your booking</p>
        <p className="text-sm font-semibold mt-1" style={{ color: primary }}>{partner.company_name}</p>
      </div>
      <div className="p-5 space-y-3 text-sm">
        <SumRow label="From" value={form.pickup_address || "—"} />
        <SumRow label="To" value={form.destination_address || "—"} />
        <SumRow label="Vehicle" value={form.vehicle_type} />
        <SumRow label="Passengers" value={`${form.passengers} pax · ${form.luggage} bags`} />
        {form.service_datetime && (
          <SumRow label="When" value={new Date(form.service_datetime).toLocaleString()} />
        )}
      </div>
      <div className="px-5 py-4 border-t border-slate-200 bg-slate-50">
        <div className="flex items-baseline justify-between">
          <span className="text-[11px] uppercase tracking-widest text-slate-500">Total</span>
          <span className="text-2xl font-serif" style={{ color: primary }} data-testid="quote-total">
            {quoting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : (price != null ? `€ ${price.toFixed(2)}` : "—")}
          </span>
        </div>
        <p className="text-[10px] text-slate-500 mt-1">All-inclusive · Free cancellation up to 24h before service</p>
      </div>
      {step < 3 && (
        <div className="p-5 space-y-2">
          <button
            onClick={onNext}
            disabled={submitting || quoting}
            className="w-full h-11 text-sm font-semibold uppercase tracking-widest transition-transform hover:-translate-y-[1px] disabled:opacity-60"
            style={{ background: accent, color: primary }}
            data-testid="continue-button"
          >
            {submitting ? "Processing…" : step === 1 ? "Continue" : "Confirm booking"}
          </button>
          {step > 1 && (
            <button onClick={onBack} className="w-full h-10 text-xs uppercase tracking-widest border border-slate-300 text-slate-600 hover:border-slate-500" data-testid="back-button">
              Back
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SumRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-[11px] uppercase tracking-widest text-slate-500 min-w-[70px]">{label}</span>
      <span className="text-slate-800 text-right text-[13px] truncate max-w-[60%]">{value}</span>
    </div>
  );
}

function VehicleGallery({ photos, label }) {
  const [idx, setIdx] = useState(0);
  if (!photos || photos.length === 0) {
    return (
      <div className="h-24 bg-slate-100 flex items-center justify-center text-slate-400 text-[10px] uppercase">Sem foto</div>
    );
  }
  const total = photos.length;
  const prev = (e) => { e.stopPropagation(); setIdx((idx - 1 + total) % total); };
  const next = (e) => { e.stopPropagation(); setIdx((idx + 1) % total); };
  return (
    <div className="relative h-24 bg-slate-100 group" data-testid="vehicle-gallery">
      <img src={photos[idx]} alt={label} className="w-full h-full object-cover" />
      {total > 1 && (
        <>
          <button type="button" onClick={prev} className="absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid="gallery-prev" aria-label="Previous photo">
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={next} className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid="gallery-next" aria-label="Next photo">
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
          <div className="absolute bottom-1 right-1 text-[9px] text-white bg-black/60 px-1.5 py-0.5 font-bold tracking-widest">{idx + 1}/{total}</div>
        </>
      )}
    </div>
  );
}
